import React, { useContext, useState, useEffect } from "react";
import { formatLargeNumber } from "../../utils/Format";
import { MonetizationContext } from "../../contexts/MonetizationContext/MonetizationContext";
import AuthContext from "../../contexts/AuthContext/AuthContext";
import "./TranslationStats.css"; // Added CSS import

const TranslationStats = ({
  fromLanguage,
  toLanguage,
  localStorageKey,
  disableHide,
}) => {
  const { monetizations, loading, error } = useContext(MonetizationContext);
  const { user, isSignedIn } = useContext(AuthContext) || {};
  const [displayMode, setDisplayMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem(localStorageKey) || "full";
      const validModes = disableHide
        ? ["full", "minimal"]
        : ["full", "minimal", "hidden"];
      return validModes.includes(savedMode) ? savedMode : "full";
    } catch (err) {
      console.error("Error accessing localStorage:", err);
      return "full";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(localStorageKey, displayMode);
    } catch (err) {
      console.error("Error saving to localStorage:", err);
    }
  }, [displayMode, localStorageKey]);

  const wrapBold = (text) => <strong>{text}</strong>;
  const wrapGreen = (text) => (
    <strong className="text-green-600 text-xl md:text-2xl">{text}</strong>
  );

  const calculateAdditionalRevenue = (fromMonetization, toMonetization) => {
    if (
      !fromMonetization ||
      !toMonetization ||
      !fromMonetization.active_users_average ||
      !toMonetization.active_users_average ||
      !fromMonetization.cpm_low ||
      !fromMonetization.cpm_high ||
      !toMonetization.cpm_low ||
      !toMonetization.cpm_high
    ) {
      return null;
    }

    const fromAvgCpm =
      (fromMonetization.cpm_low + fromMonetization.cpm_high) / 2 / 100;
    const toAvgCpm =
      (toMonetization.cpm_low + toMonetization.cpm_high) / 2 / 100;

    if (fromMonetization.active_users_average === 0 || fromAvgCpm === 0) {
      return null;
    }

    const revenue =
      1000 *
      (toAvgCpm / fromAvgCpm) *
      (toMonetization.active_users_average /
        fromMonetization.active_users_average);

    return Math.round(revenue).toLocaleString();
  };

  const getLanguageText = (language) => {
    if (!language || language.id === undefined) return null;

    const monetization = monetizations.find(
      (m) =>
        m.language.name === language.name &&
        m.platform.name.toLowerCase() === "youtube"
    );

    if (!monetization) {
      return null;
    }

    const activeUsers = monetization.active_users_average
      ? wrapBold(formatLargeNumber(monetization.active_users_average))
      : "an unknown number of";

    const averageCpm =
      monetization.cpm_low && monetization.cpm_high
        ? wrapBold(
            `$${(
              (monetization.cpm_low + monetization.cpm_high) /
              2 /
              100
            ).toFixed(2)}`
          )
        : "unknown";

    return (
      <>
        There are approximately {activeUsers} {wrapBold(language.name)} speaking
        people on YouTube. The average {wrapBold("CPM")} for a{" "}
        {wrapBold(language.name)} video is {averageCpm}.
      </>
    );
  };

  const getTranslationText = (fromLang, toLang) => {
    if (!fromLang || !toLang) return null;

    const fromMonetization = monetizations.find(
      (m) =>
        m.language.name === fromLang.name &&
        m.platform.name.toLowerCase() === "youtube"
    );
    const toMonetization = monetizations.find(
      (m) =>
        m.language.name === toLang.name &&
        m.platform.name.toLowerCase() === "youtube"
    );

    if (!fromMonetization || !toMonetization) {
      return (
        <>
          No YouTube monetization data available for {fromLang.name} to{" "}
          {toLang.name}.
        </>
      );
    }

    const additionalRevenue = calculateAdditionalRevenue(
      fromMonetization,
      toMonetization
    );

    if (!additionalRevenue) {
      return (
        <>
          Insufficient data to calculate revenue for {fromLang.name} to{" "}
          {toLang.name}.
        </>
      );
    }

    return (
      <>
        Given the audience and {wrapBold("CPM")} numbers, if a video generated{" "}
        {wrapBold(`$${(1000).toLocaleString()}`)} in {wrapBold(fromLang.name)},
        it has a potential to generate additional{" "}
        {wrapGreen(`$${additionalRevenue}`)} in {wrapBold(toLang.name)}.
      </>
    );
  };

  const fromStats =
    fromLanguage?.id !== undefined ? getLanguageText(fromLanguage) : null;
  const toStats =
    toLanguage?.id !== undefined ? getLanguageText(toLanguage) : null;
  const translationStats = getTranslationText(fromLanguage, toLanguage);

  if (loading) {
    return (
      <div className="translation-stats-loading text-center text-gray-500 italic text-base py-4">
        Loading monetization data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="translation-stats-error text-center text-red-500 italic text-base py-4">
        Error loading data: {error}
      </div>
    );
  }

  if (!fromStats && !toStats) {
    return (
      <div className="translation-stats-empty text-center text-gray-500 italic text-base py-4">
        Select languages to see stats
      </div>
    );
  }

  if (displayMode === "hidden" && !disableHide) {
    return (
      <div className="translation-stats bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-6 max-w-2xl mx-auto mt-4">
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => setDisplayMode("full")}
            onTouchStart={() => {}}
            className="text-blue-600 hover:text-blue-800 text-sm md:text-base transition-transform duration-100"
            aria-label="Show more stats"
          >
            Show More
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="translation-stats bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-6 max-w-2xl mx-auto mt-4">
      {(displayMode === "full" || displayMode === "minimal") &&
        isSignedIn &&
        user?.first_name && (
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-3">
            <span className="font-semibold text-blue-600">
              Hello {user.first_name}!
            </span>
          </p>
        )}
      {displayMode === "full" &&
        fromLanguage?.id !== undefined &&
        fromStats && (
          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            <span className="font-semibold text-blue-600">From:</span>{" "}
            {fromStats}
          </p>
        )}
      {displayMode === "full" && toLanguage?.id !== undefined && toStats && (
        <p className="text-gray-700 text-base md:text-lg leading-relaxed mt-3">
          <span className="font-semibold text-blue-600">To:</span> {toStats}
        </p>
      )}
      {(displayMode === "full" || displayMode === "minimal") &&
        fromLanguage?.id !== undefined &&
        toLanguage?.id !== undefined &&
        translationStats &&
        fromStats &&
        toStats && (
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mt-3">
            <span className="font-semibold text-blue-600">Translation:</span>{" "}
            {translationStats}
          </p>
        )}
      <div className="flex justify-end space-x-2 mt-4">
        {displayMode !== "full" && (
          <button
            onClick={() => setDisplayMode("full")}
            onTouchStart={() => {}}
            className="text-blue-600 hover:text-blue-800 text-sm md:text-base transition-transform duration-100"
            aria-label="Show more stats"
          >
            Show More
          </button>
        )}
        {displayMode === "full" && translationStats && (
          <button
            onClick={() => setDisplayMode("minimal")}
            onTouchStart={() => {}}
            className="text-blue-600 hover:text-blue-800 text-sm md:text-base transition-transform duration-100"
            aria-label="Show less stats"
          >
            Show Less
          </button>
        )}
        {!disableHide && (
          <button
            onClick={() => setDisplayMode("hidden")}
            onTouchStart={() => {}}
            className="text-blue-600 hover:text-blue-800 text-sm md:text-base transition-transform duration-100"
            aria-label="Hide stats"
          >
            Hide
          </button>
        )}
      </div>
    </div>
  );
};

export default TranslationStats;
